import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import ReportPage from '../../../Components/crude/ReportPage';
import useReportData from '../../../Components/crude/useReportData';
import { crudeApi } from '../../../helpers/crude_api_helper';
import {
  REPORT_ENDPOINTS,
  EMPTY_REPORT_FILTERS,
  CDU_LIST_ENDPOINT,
  sulfurLabel,
  yieldLabel,
  resolveImplementationCell,
} from '../../../Components/crude/reportConstants';

// Evaluation Report — GET /v1/Report/GetEvaluationReports (rhacrude_api).
//
// The per-CDU columns are generated from master CDU (GetListCDU), and each row's
// `implementations[]` is matched back by `cduId`. The API returns one entry per
// ACTIVE CDU (not just CDUs with data), so a missing/blank entry renders empty.
//
// Only the five fixed columns are sortable — the dynamic per-CDU columns are
// not (their number/order follows master CDU, so the API has no sortField for
// them; sending one returns 400).
const EvaluationReportView = () => {
  const [filters, setFilters] = useState(EMPTY_REPORT_FILTERS);
  const [cduList, setCduList] = useState([]);

  const {
    items,
    totalRows,
    loading,
    page,
    perPage,
    sortField,
    sortDirection,
    setPage,
    setPerPage,
    handleSort,
  } = useReportData({ endpoint: REPORT_ENDPOINTS.EVALUATION.LIST, filters });

  useEffect(() => {
    fetchCduList();
  }, []);

  const fetchCduList = async () => {
    try {
      const result = await crudeApi.get(CDU_LIST_ENDPOINT);
      const list = Array.isArray(result?.items) ? result.items : [];
      // `orderNumber` drives the horizontal column order.
      setCduList(
        [...list].sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0))
      );
    } catch (error) {
      toast.error(`Failed to load CDU: ${error}`);
      setCduList([]);
    }
  };

  const columns = useMemo(() => {
    const cduColumns = cduList.map(cdu => {
      // Each row exposes its own implementations[]; match by cduId.
      const cellFor = row =>
        resolveImplementationCell(
          row.implementations?.find(i => i.cduId === cdu.id)
        );

      return {
        header: cdu.cduName,
        children: [
          {
            key: `${cdu.id}-status`,
            header: 'Status',
            accessor: row => cellFor(row).status,
            sortable: false,
            align: 'center',
          },
          {
            key: `${cdu.id}-vol`,
            header: '% - Vol',
            accessor: row => cellFor(row).volPercent,
            sortable: false,
            align: 'center',
          },
          {
            key: `${cdu.id}-mb`,
            header: 'MB/bln',
            accessor: row => cellFor(row).mbPerMonth,
            sortable: false,
            align: 'center',
          },
        ],
      };
    });

    return [
      {
        key: 'CrudeName',
        header: 'Nama Minyak Mentah',
        accessor: row => row.crudeName,
      },
      {
        key: 'AssayYear',
        header: 'Tahun Assay',
        accessor: row => row.assayYear,
        align: 'center',
      },
      {
        header: 'Classification',
        children: [
          {
            key: 'SulfurClassification',
            header: 'Klasifikasi Sulfur',
            accessor: row => sulfurLabel(row.sulfurClassification),
          },
          {
            key: 'YieldClassification',
            header: 'Klasifikasi Yield',
            accessor: row => yieldLabel(row.yieldClassification),
          },
        ],
      },
      {
        key: 'CodeG5',
        header: 'Code G5',
        accessor: row => row.codeG5,
        align: 'center',
      },
      ...cduColumns,
    ];
  }, [cduList]);

  return (
    <ReportPage
      title='Evaluation Report'
      cardTitle='Evaluation Report'
      documentTitle='Evaluation Report'
      columns={columns}
      data={items}
      loading={loading}
      totalRows={totalRows}
      page={page}
      perPage={perPage}
      sortField={sortField}
      sortDirection={sortDirection}
      onChangePage={setPage}
      onChangeRowsPerPage={newPerPage => {
        setPerPage(newPerPage);
        setPage(1);
      }}
      onSort={handleSort}
      filters={filters}
      onFiltersChange={setFilters}
      endpoints={REPORT_ENDPOINTS.EVALUATION}
      exportBaseName='evaluation-report'
      emptyText='No evaluation report data found'
    />
  );
};

export default EvaluationReportView;
